import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStar,
  faPaw,
  faLeaf,
  faLandmark,
  faTree,
  faClock,
  faSun,
  faTimes,
  faCheck,
  faTag,
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import heroImage from '../../../assets/banner/flowerBan.webp';

const Page = styled.div`
  min-height: 100vh;
  padding-top: 120px;
  background-color: ${props => props.theme.colors.offWhite};
`;

const EventHero = styled.div`
  width: 100%;
  height: 420px;
  background-image: ${props => `linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.6)), url(${props.image})`};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  position: relative;

  @media (max-width: 768px) {
    height: 360px;
  }
`;

const HeroContent = styled.div`
  width: 100%;
  max-width: 1400px;
  padding: 3rem 2rem;
  color: white;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const Badge = styled.span`
  display: inline-block;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.4rem 0.8rem;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const Title = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 3rem;
  font-weight: 700;
  margin: 0.25rem 0 0.5rem;
  line-height: 1.2;
  text-shadow: 0 2px 8px rgba(0,0,0,0.4);

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const Strapline = styled.p`
  font-size: 1.25rem;
  margin: 0;
  opacity: 0.95;
  text-shadow: 0 1px 4px rgba(0,0,0,0.3);

  @media (max-width: 768px) {
    font-size: 1.05rem;
  }
`;

const Content = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 2rem 4rem;
`;

const IntroSection = styled.div`
  text-align: center;
  margin-bottom: 2.5rem;
`;

const IntroText = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  max-width: 700px;
  margin: 0 auto 1rem;
`;

const GroupSaverBox = styled.div`
  background: linear-gradient(135deg, rgba(157, 109, 73, 0.12) 0%, rgba(157, 109, 73, 0.18) 100%);
  border-left: 4px solid ${props => props.theme.colors.accent};
  padding: 1.25rem 1.5rem;
  border-radius: 10px;
  margin: 1.5rem auto 2rem;
  max-width: 700px;
  text-align: center;

  p {
    margin: 0;
    font-size: 1.1rem;
    line-height: 1.7;
    color: ${props => props.theme.colors.primary};
  }

  strong {
    color: ${props => props.theme.colors.accent};
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 2rem;
  align-items: stretch;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const OptionCard = styled.a`
  background: white;
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 10px 40px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  overflow: hidden;
  border: 2px solid transparent;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 20px 50px rgba(0,0,0,0.12);
  }

  ${props => props.$featured && `
    border-color: ${props.theme.colors.accent};
    background: linear-gradient(180deg, ${props.theme.colors.offWhite} 0%, white 15%);
    box-shadow: 0 12px 45px rgba(0,0,0,0.08);

    &:hover {
      box-shadow: 0 20px 50px rgba(0,0,0,0.12);
    }
  `}
`;

const RecommendedBadge = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.5rem 1.25rem;
  font-size: 0.85rem;
  font-weight: 700;
  border-radius: 0 16px 0 12px;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
`;

const OptionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.85rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.75rem;
  padding-right: ${props => props.$hasBadge ? '120px' : '0'};
  line-height: 1.3;

  &::after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background: ${props => props.theme.colors.accent};
    margin-top: 0.5rem;
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 1.6rem;
    padding-right: 0;
  }
`;

const OptionSubtitle = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.colors.accent};
  font-weight: 600;
  margin: 0 0 1rem;
`;

const OptionDescription = styled.div`
  font-size: 1.05rem;
  line-height: 1.75;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  flex-grow: 1;

  p {
    margin: 0 0 0.75rem;
  }

  ul {
    margin: 0.5rem 0;
    padding-left: 1.35rem;
  }

  li {
    margin: 0.4rem 0;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;

  li {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0.5rem 0;
    font-size: 1rem;
  }
`;

const ListIcon = styled(FontAwesomeIcon).attrs({ 'aria-hidden': true })`
  color: ${props => props.theme.colors.accent};
  width: 1rem;
  flex-shrink: 0;
`;

const InlineIcon = styled(FontAwesomeIcon).attrs({ 'aria-hidden': true })`
  color: ${props => props.theme.colors.accent};
  margin-right: 0.35rem;
`;

const CTAButton = styled.span`
  display: inline-block;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: 1rem 1.75rem;
  border-radius: 10px;
  font-weight: 700;
  font-size: 1.1rem;
  text-align: center;
  margin-top: auto;
  transition: all 0.25s ease;

  ${OptionCard}:hover & {
    transform: scale(1.02);
    filter: brightness(1.05);
  }
`;

const CTAButtonSecondary = styled.span`
  display: inline-block;
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: 1rem 1.75rem;
  border-radius: 10px;
  font-weight: 700;
  font-size: 1.1rem;
  text-align: center;
  margin-top: auto;
  transition: all 0.25s ease;

  ${OptionCard}:hover & {
    transform: scale(1.02);
  }
`;

const BertieHighlight = styled.div`
  background: linear-gradient(135deg, rgba(157, 109, 73, 0.12) 0%, rgba(157, 109, 73, 0.08) 100%);
  border-left: 4px solid ${props => props.theme.colors.accent};
  padding: 1rem 1.25rem;
  border-radius: 8px;
  margin: 1rem 0;
  font-size: 1rem;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
`;

/* ─── Easter Modal animations ─── */
const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(40px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0)   scale(1);    }
`;

const eggBounce = keyframes`
  0%, 100% { transform: translateY(0) rotate(-4deg); }
  50%       { transform: translateY(-8px) rotate(4deg); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(30, 20, 10, 0.65);
  backdrop-filter: blur(3px);
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  animation: ${fadeIn} 0.35s ease forwards;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 20px;
  max-width: 560px;
  width: 100%;
  position: relative;
  overflow: hidden;
  box-shadow: 0 30px 80px rgba(0,0,0,0.25);
  animation: ${slideUp} 0.45s cubic-bezier(0.22, 1, 0.36, 1) forwards;
`;

const ModalHeader = styled.div`
  background: linear-gradient(135deg, #101d40 0%, #1a2f5e 100%);
  padding: 2.25rem 2rem 1.75rem;
  text-align: center;
  position: relative;
`;

const EggRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  font-size: 1.75rem;

  span {
    display: inline-block;
    animation: ${eggBounce} 2.2s ease-in-out infinite;
  }
  span:nth-child(2) { animation-delay: 0.3s; }
  span:nth-child(3) { animation-delay: 0.6s; }
`;

const ModalEyebrow = styled.p`
  color: #9d6d49;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  margin: 0 0 0.4rem;
`;

const ModalTitle = styled.h2`
  color: #fff;
  font-family: 'Cormorant Garamond', serif;
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.25;
  margin: 0 0 0.5rem;
  text-shadow: 0 2px 6px rgba(0,0,0,0.2);

  @media (max-width: 480px) {
    font-size: 1.65rem;
  }
`;

const ModalTagline = styled.p`
  color: rgba(255,255,255,0.92);
  font-size: 1rem;
  margin: 0;
  font-weight: 500;
`;

const DiscountBanner = styled.div`
  background: linear-gradient(90deg, #9d6d49 0%, #b8894f 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.65rem 1rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: #fff;
`;

const ModalBody = styled.div`
  padding: 1.75rem 2rem 2rem;

  @media (max-width: 480px) {
    padding: 1.5rem 1.25rem 1.75rem;
  }
`;

const PerksGrid = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem 1rem;

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

const PerkItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.97rem;
  color: #101d40;
  line-height: 1.4;
`;

const PerkCheck = styled(FontAwesomeIcon)`
  color: #9d6d49;
  margin-top: 0.15rem;
  flex-shrink: 0;
`;

const GroupSaverHighlight = styled.div`
  background: linear-gradient(135deg, rgba(157, 109, 73, 0.08) 0%, rgba(157, 109, 73, 0.14) 100%);
  border-left: 4px solid #9d6d49;
  border-radius: 8px;
  padding: 0.9rem 1.1rem;
  margin-bottom: 1.5rem;
  font-size: 0.97rem;
  color: #101d40;
  line-height: 1.55;

  strong {
    color: #9d6d49;
  }
`;

const ModalCTA = styled.a`
  display: block;
  background: linear-gradient(135deg, #9d6d49 0%, #b8894f 100%);
  color: #fff;
  text-align: center;
  padding: 1.05rem 1.5rem;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 700;
  text-decoration: none;
  transition: filter 0.2s ease, transform 0.2s ease;
  margin-bottom: 0.75rem;
  box-shadow: 0 6px 20px rgba(157, 109, 73, 0.35);

  &:hover {
    filter: brightness(1.08);
    transform: translateY(-2px);
  }
`;

const ModalSkip = styled.button`
  display: block;
  width: 100%;
  background: none;
  border: none;
  color: #888;
  font-size: 0.9rem;
  cursor: pointer;
  text-align: center;
  padding: 0.25rem;
  transition: color 0.2s;

  &:hover {
    color: #555;
    text-decoration: underline;
  }
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 0.9rem;
  right: 0.9rem;
  background: rgba(255,255,255,0.2);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.2s;
  z-index: 1;

  &:hover {
    background: rgba(255,255,255,0.35);
  }
`;

/* ─── Main component ─── */

const EasterTrail = () => {
  const [searchParams] = useSearchParams();
  const skipModal = searchParams.get('from') === 'popup';

  const castleEntryUrl = 'https://www.belvoircastle.com/booking-journey?locationId=978&pageId=03c1719d-b7ff-43b1-acd9-72c6fdd56f6a';
  const parklandUrl = 'https://www.belvoircastle.com/booking-journey?locationId=978&pageId=d97b9888-0f4d-4305-a87b-b9eaf6818a6c';

  const [showModal, setShowModal] = useState(false);

  const showEasterModal = false; // Set to true to show the Easter popup again

  useEffect(() => {
    if (!showEasterModal || skipModal) return;
    const timer = setTimeout(() => setShowModal(true), 900);
    return () => clearTimeout(timer);
  }, [skipModal, showEasterModal]);

  const closeModal = () => setShowModal(false);

  const meta = {
    title: 'Easter Trail at Belvoir Castle | Parkland & Castle Entry',
    description: 'Join our Easter Trail in the Adventure Playground at Belvoir Castle. Choose The Ultimate Easter Experience with Castle & gardens, or trail and gardens. Group Saver discount available. Book your tickets.',
    canonical: 'https://www.belvoircastle.com/easter-trail',
  };

  return (
    <Page>
      {showModal && (
        <Overlay onClick={closeModal}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <CloseBtn onClick={closeModal} aria-label="Close">
                <FontAwesomeIcon icon={faTimes} />
              </CloseBtn>
              <EggRow>
                <span>🐣</span>
                <span>🌸</span>
                <span>🐰</span>
              </EggRow>
              <ModalEyebrow>Easter at Belvoir Castle</ModalEyebrow>
              <ModalTitle>Your Ultimate Easter Adventure Starts Here</ModalTitle>
              <ModalTagline>The UK's most flexible family Easter package</ModalTagline>
            </ModalHeader>

            <DiscountBanner>
              <FontAwesomeIcon icon={faTag} />
              Group Saver discount — the more you add, the more you save
            </DiscountBanner>

            <ModalBody>
              <PerksGrid>
                <PerkItem>
                  <PerkCheck icon={faCheck} />
                  <span>Easter Trail in the Adventure Playground</span>
                </PerkItem>
                <PerkItem>
                  <PerkCheck icon={faCheck} />
                  <span>Full Castle entry included</span>
                </PerkItem>
                <PerkItem>
                  <PerkCheck icon={faCheck} />
                  <span>Bertie Bear Castle Trail for the kids</span>
                </PerkItem>
                <PerkItem>
                  <PerkCheck icon={faCheck} />
                  <span>Beautiful gardens &amp; parkland</span>
                </PerkItem>
                <PerkItem>
                  <PerkCheck icon={faCheck} />
                  <span>Animal Park — FREE this Easter</span>
                </PerkItem>
                <PerkItem>
                  <PerkCheck icon={faCheck} />
                  <span>A full day of family fun</span>
                </PerkItem>
              </PerksGrid>

              <GroupSaverHighlight>
                <strong>Bring the whole family and save more.</strong> Our Group Saver discount means every extra ticket costs you less — perfect for big family days out this Easter.
              </GroupSaverHighlight>

              <ModalCTA href={castleEntryUrl} target="_blank" rel="noopener noreferrer" onClick={closeModal}>
                🐣 Book the Ultimate Easter Experience →
              </ModalCTA>
              <ModalSkip onClick={closeModal}>
                Just browsing — show me all options
              </ModalSkip>
            </ModalBody>
          </Modal>
        </Overlay>
      )}

      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta name="keywords" content="Easter Trail, Adventure Playground, Belvoir Castle, Easter family day out, Bertie Bear, Castle entry, gardens, Group Saver, Easter activities, Leicestershire" />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
      </Helmet>

      <EventHero image={heroImage}>
        <HeroContent>
          <Badge>Easter at Belvoir</Badge>
          <Title>Easter Trail</Title>
          <Strapline>Hop into spring with our Easter Trail — choose The Ultimate Easter Experience or trail &amp; gardens</Strapline>
        </HeroContent>
      </EventHero>

      <Content>
        <IntroSection>
          <IntroText>
            Our Easter Trail is in the <strong>Adventure Playground</strong>. Choose <strong>The Ultimate Easter Experience</strong> for a full day out — Castle, gardens and the Bertie Bear Castle Trail — or <strong>Easter Trail and Gardens</strong> for the trail in the playground plus gardens access.
          </IntroText>
        </IntroSection>

        <GroupSaverBox>
          <p><strong>Enjoy the Belvoir Group Saver discount</strong> — the more you add, the more you save. Bring family or friends and make the most of your day.</p>
        </GroupSaverBox>

        <CardsGrid>
          <OptionCard href={castleEntryUrl} target="_blank" rel="noopener noreferrer" $featured>
            <RecommendedBadge><FontAwesomeIcon icon={faStar} /> Best value</RecommendedBadge>
            <OptionTitle $hasBadge>The Ultimate Easter Experience – Castle & Gardens</OptionTitle>
            <OptionSubtitle>Everything included for your best day out</OptionSubtitle>
            <OptionDescription>
              <p>Make the most of Easter at Belvoir with our full-day experience. Follow the Easter Trail in the Adventure Playground, then step inside the Castle for the Bertie Bear Castle Trail.</p>
              <BertieHighlight>
                <InlineIcon icon={faPaw} /> Includes the Bertie Bear Castle Trail — fun for the whole family.
              </BertieHighlight>
              <FeatureList>
                <li><ListIcon icon={faLeaf} /> Easter Trail in the Adventure Playground</li>
                <li><ListIcon icon={faLandmark} /> Castle entry &amp; Bertie Bear Castle Trail</li>
                <li><ListIcon icon={faTree} /> Gardens and parkland</li>
                <li><ListIcon icon={faClock} /> Full day out — best value</li>
              </FeatureList>
              <BertieHighlight>
                Add Animal Park for free — exclusive Easter offer
              </BertieHighlight>
            </OptionDescription>
            <CTAButton>Book the Ultimate Experience →</CTAButton>
          </OptionCard>

          <OptionCard href={parklandUrl} target="_blank" rel="noopener noreferrer">
            <OptionTitle>Easter Trail and Gardens</OptionTitle>
            <OptionSubtitle>Trail in the playground + gardens access</OptionSubtitle>
            <OptionDescription>
              <p>Follow the Easter Trail in the Adventure Playground and enjoy access to the gardens too. Perfect if you’re after a shorter visit — the trail is in the playground and you can explore the gardens as well.</p>
              <FeatureList>
                <li><ListIcon icon={faLeaf} /> Easter Trail in the Adventure Playground</li>
                <li><ListIcon icon={faTree} /> Gardens access included</li>
                <li><ListIcon icon={faSun} /> Great for a spring stroll</li>
              </FeatureList>
              <BertieHighlight>
                Add Animal Park for £4.50
              </BertieHighlight>
            </OptionDescription>
            <CTAButtonSecondary>Book Trail &amp; Gardens →</CTAButtonSecondary>
          </OptionCard>
        </CardsGrid>
      </Content>

      <Footer />
    </Page>
  );
};

export default EasterTrail;
